<script setup lang="ts">
import { computed } from 'vue';
import IconTwitter from '~icons/bxl/twitter';
import IconGithub from '~icons/bxl/github';
import IconLinkedin from '~icons/bxl/linkedin';
import IconGlobe from '~icons/mdi/globe';
import type { GetProfileResponse } from '~/utilities/pyscript-api-models';

interface Props {
  usernameOrId: string;
  user: GetProfileResponse | null;
  isOwner: boolean;
}
const props = defineProps<Props>();

defineEmits<{
  (e: 'update:show', show: boolean): void;
}>();

const hasSocials = computed(() => {
  if (!props.user) return false;
  return Object.values(props.user.socials).some((value) => !!value);
});

function generateSocial(name: string, username: string) {
  if (!username) return '';
  switch (name) {
    case 'twitter':
      return `twitter.com/${username}`;
    case 'github':
      return `github.com/${username}`;
    case 'linkedin':
      return `linkedin.com/in/${username}`;
    case 'website':
      return `${username.replace(/^https?:\/\//i, '')}`;
  }
}
</script>

<template>
  <section class="flex flex-col items-stretch">
    <div v-if="user?.bio || hasSocials" class="flex h-full flex-col gap-6">
      <div v-if="user?.bio" class="flex flex-col flex-nowrap items-start">
        <p class="leading-[1.4]">{{ user.bio }}</p>
      </div>

      <div v-if="user && hasSocials">
        <ul class="flex flex-col gap-1">
          <template v-for="(value, key) in user.socials" :key="key">
            <li v-if="value" class="flex items-center gap-2 text-sm">
              <span>
                <IconTwitter v-if="key === 'twitter' && value" aria-hidden="true" />
                <IconGithub v-else-if="key === 'github' && value" aria-hidden="true" />
                <IconLinkedin v-else-if="key === 'linkedin' && value" aria-hidden="true" />
                <IconGlobe v-else-if="key === 'website' && value" aria-hidden="true" />
              </span>

              <a
                :href="`https://${generateSocial(key, value)}`"
                target="_blank"
                rel="noopener noreferrer"
                class="align-center gap-2 text-sm hover:text-slate-blue-500 hover:underline"
              >
                {{ generateSocial(key, value) }}
              </a>
            </li>
          </template>
        </ul>
      </div>
    </div>
  </section>
</template>
